/**
 * sCurveSSOTAdapter.js
 *
 * Phase 5D, Step 4 — Composition layer for the S-Curve. Combines TWO
 * fundamentally different kinds of time axis into one aligned series:
 *
 *   Actual / EAC  ← timeSeriesSSOTAdapter (Phase 5A)
 *                    Forecast Observation Dates — irregular points, each
 *                    one an actual committed forecast_snapshots row.
 *
 *   Planned       ← plannedBudgetAdapter (Phase 5D, Step 2)
 *                    Project Timeline — regular monthly periods, entered
 *                    directly by the user in Scheduling.jsx.
 *
 * These are NOT the same kind of "time" and this Adapter does NOT force
 * them to be. It performs a date UNION and reports `null` for whichever
 * source has no value at a given time point — never interpolates,
 * never estimates, never treats a Forecast Revision snapshot date as if
 * it were a calendar month or vice versa.
 *
 * This module computes NOTHING financial — every value it returns is
 * read verbatim from the two Owner-level Adapters it composes. Its only
 * job is the date union + null-filling + metadata annotation.
 */

import { executeTimeSeriesQuery } from './timeSeriesSSOTAdapter'
import { getPlannedBudgetSeries } from './plannedBudgetAdapter'

/**
 * Builds the composed S-Curve result for a project.
 *
 * @param {string} projectId
 * @param {string[]} forecastDates — the Forecast Observation Dates to
 *   pull Actual/EAC from (same input timeSeriesSSOTAdapter/
 *   buildCumulativeSeries already expect — this Adapter does not decide
 *   which dates to request, the caller does, same as every other Adapter
 *   in this codebase).
 * @returns {SCurveResult}
 *
 *   SCurveResult {
 *     series: Array<{
 *       time: string
 *       planned_budget: number | null
 *       actual: number | null
 *       eac: number | null
 *       hasSnapshot: boolean | null   // null when this time point has no
 *                                      // Actual/EAC source at all (a
 *                                      // Planned-only point) — false
 *                                      // when it DOES have an Actual/EAC
 *                                      // point but that point itself was
 *                                      // a fallback (no real forecast
 *                                      // snapshot existed for that date)
 *       sourcesPresent: {
 *         planned: boolean
 *         actualEac: boolean
 *       }
 *     }>
 *     meta: {
 *       plannedSource: string
 *       actualSource: string
 *       eacSource: string
 *       timeAlignment: string
 *     }
 *   }
 */
export async function buildSCurveResult(projectId, forecastDates) {
  const [plannedResult, actualEacResult] = await Promise.all([
    getPlannedBudgetSeries(projectId),
    executeTimeSeriesQuery(projectId, forecastDates, {
      dataset: 'time_series',
      measures: ['actual', 'eac'],
      dimension: null,
      comparison: null,
      time: null,
      filters: {},
    }),
  ])

  // Index both sources by their own time key — no reformatting, no
  // reinterpretation of one axis in terms of the other.
  const plannedByTime = new Map(
    plannedResult.series.map(p => [p.time, p.rows[0]?.values?.planned_budget ?? null])
  )
  const actualEacByTime = new Map(
    actualEacResult.series.map(p => [p.time, {
      actual: p.rows[0]?.values?.actual ?? null,
      eac: p.rows[0]?.values?.eac ?? null,
      hasSnapshot: p.hasSnapshot,
    }])
  )

  // Date union — every time point present in EITHER source, sorted
  // chronologically. This does NOT create a regular monthly grid and
  // does NOT snap Forecast Observation Dates onto Planned's monthly
  // periods (or vice versa) — a 2026-03-15 forecast_date and a
  // 2026-03-01 period_month remain two DISTINCT points on this axis,
  // exactly as they are two distinct kinds of date. Any visual choice to
  // group/align them further is a Renderer concern, not this Adapter's.
  const allTimes = new Set([...plannedByTime.keys(), ...actualEacByTime.keys()])
  const sortedTimes = [...allTimes].sort()

  const series = sortedTimes.map(time => {
    const plannedValue = plannedByTime.has(time) ? plannedByTime.get(time) : null
    const actualEacPoint = actualEacByTime.get(time)

    return {
      time,
      planned_budget: plannedValue,
      actual: actualEacPoint ? actualEacPoint.actual : null,
      eac: actualEacPoint ? actualEacPoint.eac : null,
      // null (not false) when this time point simply has no Actual/EAC
      // source at all — distinct from false, which means an Actual/EAC
      // point EXISTS here but was itself a fallback value. Conflating
      // "no source" with "fallback source" would misrepresent a
      // Planned-only calendar month as if it were a failed forecast
      // lookup, which it is not.
      hasSnapshot: actualEacPoint ? actualEacPoint.hasSnapshot : null,
      sourcesPresent: {
        planned: plannedByTime.has(time),
        actualEac: actualEacByTime.has(time),
      },
    }
  })

  return {
    series,
    meta: {
      plannedSource: 'plannedBudgetAdapter (cash_in_planning.planned_budget, monthly Project Timeline)',
      actualSource: 'timeSeriesSSOTAdapter (buildCumulativeSeries, cumulative Actual at each Forecast Observation Date)',
      eacSource: 'timeSeriesSSOTAdapter (buildCumulativeSeries, EAC at each Forecast Observation Date)',
      timeAlignment: 'union — Planned (monthly Project Timeline) and Actual/EAC (irregular Forecast Observation Dates) are NOT merged onto a common grid; each time point reports null for whichever source has no value there',
    },
  }
}
